import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { observer } from "mobx-react";
import { signalsData } from "Stores/signalsData";
import { agentsData } from "Stores/agentsData";
import RotatingMessageLoader from "Components/common/RotatingMessageLoader";
import useRotatingMessages from "Components/common/hooks/useRotatingMessages";
import SignalIcon from "../SignalIcon";
import SignalPicker from "../SignalPicker";
import { directCompetitors } from "../signalMeta";
import { CustomMatch } from "./CustomEvidence";
import {
  EXAMPLES, MAX_KEYWORDS, phraseParts, fillPhrase, slotsMissing, textProblem, addKeyword, removeKeyword, sourcesText,
  definitionFor, appliesToOf, costLine, customOf, suggestedTargetOf, FREQUENCIES, periodOf, capitalised
} from "./customSignalMeta";
import "./customSignals.scss";

const STEPS = ["Describe it", "Who to watch", "Preview", "Start tracking"];
const COMPETITORS = "competitors";
const DRAFT_MESSAGES = [
  "Reading what you asked for…",
  "Picking the words people use for it…",
  "Working out where to look…",
  "Putting it in plain words…"
];
const PREVIEW_MESSAGES = [
  "Reading recent posts on up to 5 of them…",
  "Looking for what you described…",
  "Checking each quote really says it…",
  "Skipping near misses…",
  "Lining up the best matches…"
];

// A picker value is "<type>:<id>" so account and contact lists with the same id can't collide.
const valueOf = target => (target && target.type ? (target.type === COMPETITORS ? COMPETITORS : `${target.type}:${target.id}`) : null);
const targetOf = value => {
  if (!value) return null;
  if (value === COMPETITORS) return { type: COMPETITORS };
  const cut = value.indexOf(":");
  return { type: value.slice(0, cut), id: value.slice(cut + 1) };
};

/** Step 1, before the draft: one text box, or a starter with its blanks highlighted. */
const Describe = ({ template, values, setValues, text, setText, onFreeText, onExample }) => {
  const placeholder = useRotatingMessages(EXAMPLES, !template && !text, 3200);
  if (template) {
    return (
      <div className="cs-describe">
        <label className="cs-label">Fill in the blanks</label>
        <div className="cs-phrase">
          {phraseParts(template.phrase).map((p, i) =>
            p.slot ? (
              <input key={`${p.slot}-${i}`} className="cs-slot" aria-label={p.slot} placeholder={p.slot} value={values[p.slot] || ""}
                onChange={e => setValues({ ...values, [p.slot]: e.target.value })} autoFocus={i === 1} />
            ) : (
              <span key={`t-${i}`}>{p.text}</span>
            )
          )}
        </div>
        {template.example && <small className="sg-hint">For example: {template.example}</small>}
        <button type="button" className="sg-link cs-switch" onClick={onFreeText}>Write it your own way</button>
      </div>
    );
  }
  return (
    <div className="cs-describe">
      <label className="cs-label" htmlFor="cs-text">What do you want to hear about?</label>
      <textarea id="cs-text" className="sg-input cs-text" rows={4} maxLength={500} autoFocus value={text}
        placeholder={`e.g. ${placeholder}`} onChange={e => setText(e.target.value)} />
      <small className="sg-hint">Say it like you'd tell a teammate. You decide what matters, Salesgear reads the posts.</small>
      <div className="cs-examples">
        {EXAMPLES.map(ex => (
          <button key={ex} type="button" className="sg-chip" onClick={() => onExample(ex)}>{ex.replace(/^Tell me when /, "")}</button>
        ))}
      </div>
    </div>
  );
};

/** Step 1, after the draft: what we understood, every part editable. */
const Understanding = ({ definition, onChange, onReword }) => {
  const [word, setWord] = useState("");
  const d = definition;
  const add = () => {
    onChange({ ...d, keywords: addKeyword(d.keywords, word) });
    setWord("");
  };
  return (
    <div className="cs-under">
      <div className="cs-under__lead"><SignalIcon id="sparkle" size={15} /> Here's what we'll look for. Change anything that's off.</div>
      <label className="cs-label" htmlFor="cs-name">Name</label>
      <input id="cs-name" className="sg-input" maxLength={80} value={d.name} onChange={e => onChange({ ...d, name: e.target.value })} />
      <label className="cs-label" htmlFor="cs-cond">Fires when…</label>
      <textarea id="cs-cond" className="sg-input cs-text" rows={3} maxLength={500} value={d.condition} onChange={e => onChange({ ...d, condition: e.target.value })} />
      <label className="cs-label">Words we search for</label>
      <div className="cs-kws">
        {d.keywords.map(k => (
          <span key={k} className="sg-atag">{k}<button type="button" aria-label={`Remove ${k}`} onClick={() => onChange({ ...d, keywords: removeKeyword(d.keywords, k) })}><SignalIcon id="x" size={12} /></button></span>
        ))}
        {d.keywords.length < MAX_KEYWORDS && (
          <input className="sg-input cs-kws__add" placeholder="Add a word or phrase" value={word} onChange={e => setWord(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); add(); } }} onBlur={() => word.trim() && add()} />
        )}
      </div>
      {sourcesText(d.sources) && <div className="cs-where"><b>Where we look:</b> {sourcesText(d.sources)}, from the last month.</div>}
      {onReword && <button type="button" className="sg-link cs-switch" onClick={onReword}>Say it differently</button>}
    </div>
  );
};

/**
 * Create (or edit) a custom signal: describe it in your words, check what we understood, pick who to watch, see a free
 * preview on up to 5 of them, then start tracking. `saved` is the SavedSignalView when editing; `template` a starter;
 * `startText` a prefilled ask. onClose(savedView | null).
 */
const CustomSignalDrawer = ({ template: startTemplate = null, startText = "", saved = null, onClose }) => {
  const editing = !!(saved && saved.signal);
  const was = editing ? customOf(saved) : null;
  const [step, setStep] = useState(0);
  const [template, setTemplate] = useState(startTemplate);
  const [values, setValues] = useState({});
  const [text, setText] = useState(startText);
  const [definition, setDefinition] = useState(editing ? definitionFor(was.definition, was.target.type) : null);
  const [targetValue, setTargetValue] = useState(editing ? valueOf(was.target) : null);
  const [preview, setPreview] = useState(null); // {data} | {error}
  const [previewing, setPreviewing] = useState(false);
  // What the draft thought it watches: "competitors" preselects them, "contacts" opens the picker on contact lists.
  const [suggested, setSuggested] = useState("");
  const [frequency, setFrequency] = useState(editing ? was.frequency : "weekly");
  const [everyDays, setEveryDays] = useState(editing && was.everyDays ? was.everyDays : 3);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    agentsData.fetchAccountLists();
    agentsData.fetchContactLists();
    if (!signalsData.competitors) signalsData.fetchCompetitors();
    const onKey = e => { if (e.key === "Escape") onClose(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []); // eslint-disable-line

  const ask = template ? fillPhrase(template.phrase, values) : text;
  const askProblem = template ? (slotsMissing(template.phrase, values).length ? "Fill in the blanks first." : "") : textProblem(text);
  const target = targetOf(targetValue);
  const def = definition && target ? definitionFor(definition, target.type) : definition;
  const defProblem = !def ? "" : !def.name.trim() ? "Give it a name." : !def.condition.trim() ? "Say when it should fire." : !def.keywords.length ? "Add at least one word to search for." : "";

  const accountLists = agentsData.accountLists || [];
  const contactLists = agentsData.contactLists || [];
  const competitorCount = directCompetitors(((signalsData.competitors || {}).competitors)).length;
  const options = useMemo(() => {
    const companies = [
      { value: COMPETITORS, label: "Your competitors", hint: competitorCount ? `${competitorCount}` : "", group: "Companies" },
      ...accountLists.map(l => ({ value: `account_list:${l.id}`, label: l.name, hint: l.count != null ? `${Number(l.count).toLocaleString()}` : "", group: "Companies" }))
    ];
    const people = contactLists.map(l => ({ value: `contact_list:${l.id}`, label: l.name, hint: l.count != null ? `${Number(l.count).toLocaleString()}` : "", group: "People" }));
    return suggested === "contacts" ? people.concat(companies) : companies.concat(people);
  }, [accountLists, contactLists, competitorCount, suggested]);
  const picked = options.find(o => o.value === targetValue);
  const records = !target ? null : target.type === COMPETITORS ? competitorCount : picked && picked.hint ? Number(picked.hint.replace(/,/g, "")) : null;
  const pd = (preview && preview.data) || null;
  const cost = costLine({ appliesTo: target ? appliesToOf(target.type) : (def || {}).appliesTo, records, perWeek: pd ? pd.estimatedCreditsPerWeek : null, frequency, everyDays });
  const watchingLabel = picked ? picked.label : (editing && saved.scopeLabel) || "";

  const runDraft = async () => {
    if (askProblem) { setError(askProblem); return; }
    setBusy(true);
    setError("");
    const out = await signalsData.draftCustom(ask.trim());
    setBusy(false);
    if (out.error) { setError(out.error); return; }
    const d = out.data || {};
    setDefinition(definitionFor(d, d.appliesTo === "contacts" ? "contact_list" : "account_list"));
    const sug = suggestedTargetOf(d, ask);
    setSuggested(sug);
    if (sug === "competitors" && !targetValue) setTargetValue(COMPETITORS);
  };

  const runPreview = async () => {
    setPreviewing(true);
    setPreview(null);
    const out = await signalsData.previewCustom(def, target);
    setPreview(out);
    setPreviewing(false);
  };
  const go = n => {
    setError("");
    setStep(n);
    if (n === 2 && !preview && !previewing) runPreview();
  };
  // A new definition or target makes the last preview stale.
  useEffect(() => { setPreview(null); }, [targetValue, definition]);

  const save = async () => {
    setBusy(true);
    setError("");
    const body = { definition: { ...def, name: capitalised(def.name) }, target, frequency, ...(frequency === "custom" ? { everyDays: Number(everyDays) || 7 } : {}) };
    const out = await signalsData.saveCustom(editing ? saved.signal.id : null, body);
    setBusy(false);
    if (out.error) setError(out.error);
    else onClose(out.data);
  };

  // What the footer's main button does on each step.
  let next;
  if (step === 0 && !definition) next = { label: busy ? "Reading…" : "Continue", run: runDraft, disabled: busy || !!askProblem };
  else if (step === 0) next = { label: "Next: who to watch", run: () => (defProblem ? setError(defProblem) : go(1)), disabled: false };
  else if (step === 1) next = { label: "Next: preview", run: () => go(2), disabled: !target };
  else if (step === 2) next = { label: "Next", run: () => go(3), disabled: previewing };
  else next = { label: busy ? "Saving…" : editing ? "Save changes" : "Start tracking", run: save, disabled: busy };
  const canReach = n => n === 0 || (def && !defProblem && (n === 1 || target));

  return ReactDOM.createPortal(
    <div className="sg-drawer-bg" onClick={() => onClose(null)}>
      <div className="sg-drawer sg-drawer--wide cs-drawer" onClick={e => e.stopPropagation()} role="dialog" aria-label={editing ? "Edit your signal" : "Create your own signal"}>
        <div className="sg-drawer__head">
          <span className="sg-tile sg-tile--lg cus"><SignalIcon id="sparkle" size={22} /></span>
          <div className="sg-drawer__title">
            <h2>{editing ? `Edit: ${saved.displayName || def.name}` : "Create your own signal"}</h2>
            <p>You decide what to watch for. Salesgear reads the posts and drafts the outreach.</p>
          </div>
          <button className="sg-x" onClick={() => onClose(null)} aria-label="Close">×</button>
        </div>

        <ol className="cs-steps">
          {STEPS.map((label, i) => (
            <li key={label} className={`${i === step ? "is-now" : ""} ${i < step ? "is-done" : ""}`}>
              <button type="button" disabled={!canReach(i) || busy} onClick={() => go(i)}>
                <span className="cs-steps__n">{i < step ? <SignalIcon id="check" size={12} /> : i + 1}</span>{label}
              </button>
            </li>
          ))}
        </ol>

        <div className="sg-drawer__body cs-body">
          {step === 0 && !definition && !busy && (
            <Describe template={template} values={values} setValues={setValues} text={text} setText={setText}
              onFreeText={() => { setText(fillPhrase(template.phrase, values).replace(/\[[^\]]+\]/g, "").trim()); setTemplate(null); }}
              onExample={setText} />
          )}
          {step === 0 && !definition && busy && (
            <RotatingMessageLoader title="Getting it right" messages={DRAFT_MESSAGES} hint={`“${ask.trim()}”`} />
          )}
          {step === 0 && definition && (
            <Understanding definition={def} onChange={setDefinition} onReword={editing ? null : () => { setDefinition(null); setError(""); }} />
          )}

          {step === 1 && (
            <div className="cs-who">
              <label className="cs-label">Who to watch</label>
              <SignalPicker value={targetValue} options={options} onChange={setTargetValue} placeholder={suggested === "contacts" ? "Pick a contact list" : "Pick a list or your competitors"} searchable className="sg-pick--wide"
                defaultOpen={suggested === "contacts" && !targetValue}
                emptyText="You have no lists yet. Create a company or contact list, then come back to pick it." />
              <small className="sg-hint">
                {target && appliesToOf(target.type) === "contacts"
                  ? "We read what these people post themselves."
                  : "We read each company's page and what their people post."}
              </small>
              {target && <div className="cs-cost"><SignalIcon id="coin" size={14} /> {cost}</div>}
              {target && def && def.appliesTo !== (definition && definition.appliesTo) && (
                <div className="sg-warn">You described {definition.appliesTo === "contacts" ? "people" : "companies"}, and picked {appliesToOf(target.type) === "contacts" ? "people" : "companies"}. That works, we'll read {sourcesText(def.sources)}.</div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="cs-preview">
              {previewing && (
                <RotatingMessageLoader title={`Trying “${def.name}” on ${watchingLabel || "your list"}`} messages={PREVIEW_MESSAGES} hint="Free. Nothing is saved. Usually under a minute." />
              )}
              {!previewing && preview && preview.error && (
                <div className="sg-feed__empty" role="alert">
                  <b>The preview didn't finish</b>
                  <span>{preview.error}</span>
                  <button className="sg-btn sg-btn--small sg-btn--ghost sg-feed__retry" onClick={runPreview}>Try again</button>
                </div>
              )}
              {!previewing && pd && (
                <>
                  <div className="cs-preview__head">
                    <b>{pd.matches && pd.matches.length ? `${pd.matches.length} of ${pd.checked} fired` : `Checked ${pd.checked || 0}`}</b>
                    <span>Free preview on up to 5 from {watchingLabel || "your list"}</span>
                    <span className="sg-grow" />
                    <button className="sg-link" onClick={runPreview}>Run again</button>
                  </div>
                  {pd.unreadable && pd.unreadable.length > 0 && (
                    <p className="sg-quietline">
                      Couldn't find a LinkedIn page for {pd.unreadable.join(", ")}, so {pd.unreadable.length === 1 ? "it wasn't" : "they weren't"} checked.
                    </p>
                  )}
                  {!pd.checked && pd.unreadable && pd.unreadable.length ? null : pd.matches && pd.matches.length ? (
                    <div className="cs-matches">{pd.matches.map((m, i) => <CustomMatch key={m.recordId || i} match={m} />)}</div>
                  ) : (
                    <div className="sg-feed__empty">
                      <b>Nothing in the last month on these {pd.checked || 5}.</b>
                      <span>Try broader words or a bigger list. Or start tracking anyway: we check every {periodOf(frequency, everyDays)}.</span>
                      <button className="sg-btn sg-btn--small sg-btn--ghost sg-feed__retry" onClick={() => go(0)}>Change the words</button>
                    </div>
                  )}
                  <div className="cs-cost"><SignalIcon id="coin" size={14} /> {cost}</div>
                </>
              )}
            </div>
          )}

          {step === 3 && def && (
            <div className="cs-start">
              <dl className="cs-sum">
                <div><dt>Signal</dt><dd>{capitalised(def.name)}</dd></div>
                <div><dt>Fires when</dt><dd>{def.condition}</dd></div>
                <div><dt>Watching</dt><dd>{watchingLabel}</dd></div>
                <div>
                  <dt>Checks</dt>
                  <dd>
                    <div className="sg-chips">
                      {FREQUENCIES.map(([value, label]) => (value === "custom" && frequency === "custom" ? (
                        <label key={value} className="sg-chip on cs-every">
                          Every <input type="number" min="1" max="30" value={everyDays} onChange={e => setEveryDays(e.target.value)} aria-label="Check every how many days" /> days
                        </label>
                      ) : (
                        <button key={value} type="button" className={`sg-chip ${frequency === value ? "on" : ""}`} onClick={() => setFrequency(value)}>{label}</button>
                      )))}
                    </div>
                    <small className="cs-sum__note">{editing ? `Next check ${periodOf(frequency, everyDays)} after the last.` : "The first one runs right after you start."}</small>
                  </dd>
                </div>
                <div><dt>Cost</dt><dd>{cost}</dd></div>
              </dl>
              <ol className="cs-what">
                <li>Each match lands in your feed with the quote and a link to the post.</li>
                <li>Salesgear drafts the outreach. You decide what to send.</li>
                <li>Pause or stop it any time from its page.</li>
              </ol>
            </div>
          )}

          {error && <div className="sg-error">{error}</div>}
        </div>

        <div className="sg-drawer__foot">
          {step === 0 && !definition ? (
            <button className="sg-btn sg-btn--ghost" onClick={() => onClose(null)}>Cancel</button>
          ) : (
            <button className="sg-btn sg-btn--ghost" disabled={busy} onClick={() => (step === 0 ? onClose(null) : go(step - 1))}>{step === 0 ? "Cancel" : "Back"}</button>
          )}
          {editing && step < 3 && (
            <button className="sg-btn sg-btn--ghost" disabled={busy || !!defProblem || !target} onClick={save}>{busy ? "Saving…" : "Save changes"}</button>
          )}
          <button className="sg-btn" disabled={next.disabled} onClick={next.run}>{next.label}</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default observer(CustomSignalDrawer);
